import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import Accordion from "@material-ui/core/Accordion";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { Alert, Autocomplete } from "@material-ui/lab";
import { makeStyles } from "@material-ui/core/styles";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  resolvedList: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    width: "100%",
  },
  resolvedItem: {
    fontSize: 13,
    color: theme.palette.text.secondary,
  },
  missingSection: {
    marginTop: theme.spacing(2),
  },
}));

const DocumentSendModal = ({
  open,
  onClose,
  ticketId,
  preselectedTemplateId,
}) => {
  const classes = useStyles();

  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [sending, setSending] = useState(false);
  const [resolution, setResolution] = useState(null);
  const [manualValues, setManualValues] = useState({});
  const [caption, setCaption] = useState("");
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  const clearPreview = () => {
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  useEffect(() => {
    if (!open) {
      setSelectedTemplate(null);
      setResolution(null);
      setManualValues({});
      setCaption("");
      clearPreview();
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/document-templates/list");
        setTemplates(data);
        if (preselectedTemplateId) {
          const preselected = data.find(
            (t) => t.id === +preselectedTemplateId
          );
          if (preselected) {
            setSelectedTemplate(preselected);
          }
        }
      } catch (err) {
        toastError(err);
      }
    })();
  }, [open, preselectedTemplateId]);

  useEffect(() => {
    if (!open || !selectedTemplate || !ticketId) {
      setResolution(null);
      return;
    }
    (async () => {
      setResolving(true);
      try {
        const { data } = await api.post(
          `/document-templates/${selectedTemplate.id}/resolve`,
          { ticketId }
        );
        setResolution(data);
        setManualValues({});
      } catch (err) {
        toastError(err);
        setResolution(null);
      } finally {
        setResolving(false);
      }
    })();
  }, [open, selectedTemplate, ticketId]);

  // Qualquer mudança nos valores invalida o preview gerado
  useEffect(() => {
    clearPreview();
  }, [manualValues, selectedTemplate, resolution]);

  const missing = resolution?.missing || [];
  const resolvedEntries = Object.entries(resolution?.variables || {});
  const allFilled = missing.every(
    (item) => (manualValues[item.key] || "").trim() !== ""
  );

  const handlePreview = async () => {
    if (!selectedTemplate) return;
    setPreviewing(true);
    try {
      const { data } = await api.post(
        `/document-templates/${selectedTemplate.id}/preview`,
        { ticketId, manualValues },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(
        new Blob([data], { type: "application/pdf" })
      );
      setPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
    } catch (err) {
      // resposta de erro chega como Blob quando responseType é blob
      if (err?.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          err.response.data = parsed;
        } catch (e) {
          // mantém o erro original
        }
      }
      toastError(err);
    } finally {
      setPreviewing(false);
    }
  };

  const handleSend = async () => {
    if (!selectedTemplate) return;
    setSending(true);
    try {
      await api.post(`/document-templates/${selectedTemplate.id}/generate`, {
        ticketId,
        manualValues,
        caption,
      });
      toast.success(i18n.t("documentTemplates.toasts.sent"));
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={previewUrl ? "md" : "sm"}
      fullWidth
    >
      <DialogTitle>
        {i18n.t("documentTemplates.sendModal.title")}
      </DialogTitle>
      <DialogContent dividers>
        <Autocomplete
          options={templates}
          getOptionLabel={(option) => option.name || ""}
          value={selectedTemplate}
          onChange={(e, value) => setSelectedTemplate(value)}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              size="small"
              label={i18n.t("documentTemplates.sendModal.selectTemplate")}
            />
          )}
        />

        {resolving && (
          <div style={{ textAlign: "center", padding: 16 }}>
            <CircularProgress size={24} />
          </div>
        )}

        {resolution && !resolving && (
          <>
            {resolution.cliente === null && (
              <Alert severity="warning" style={{ marginTop: 12 }}>
                {i18n.t("documentTemplates.sendModal.noClienteLinked")}
              </Alert>
            )}

            {resolvedEntries.length > 0 && (
              <Accordion
                variant="outlined"
                style={{ marginTop: 12 }}
                defaultExpanded={false}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">
                    {i18n.t("documentTemplates.sendModal.resolvedFields")} (
                    {resolvedEntries.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <div className={classes.resolvedList}>
                    {resolvedEntries.map(([key, value]) => (
                      <Typography
                        key={key}
                        className={classes.resolvedItem}
                        component="div"
                      >
                        <strong>{key}</strong>: {value}
                      </Typography>
                    ))}
                  </div>
                </AccordionDetails>
              </Accordion>
            )}

            {missing.length > 0 && (
              <div className={classes.missingSection}>
                <Typography variant="subtitle2" gutterBottom>
                  {i18n.t("documentTemplates.sendModal.missingFields")}
                </Typography>
                {missing.map((item) => (
                  <TextField
                    key={item.key}
                    fullWidth
                    required
                    size="small"
                    variant="outlined"
                    margin="dense"
                    label={item.label}
                    value={manualValues[item.key] || ""}
                    onChange={(e) =>
                      setManualValues((prev) => ({
                        ...prev,
                        [item.key]: e.target.value,
                      }))
                    }
                  />
                ))}
              </div>
            )}

            <TextField
              fullWidth
              size="small"
              variant="outlined"
              margin="dense"
              style={{ marginTop: 16 }}
              label={i18n.t("documentTemplates.sendModal.caption")}
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
            />

            {previewUrl && (
              <iframe
                src={previewUrl}
                title={i18n.t("documentTemplates.sendModal.previewTitle")}
                style={{
                  width: "100%",
                  height: "60vh",
                  marginTop: 16,
                  border: "1px solid #ccc",
                  borderRadius: 4,
                }}
              />
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={sending}>
          {i18n.t("documentTemplates.sendModal.cancel")}
        </Button>
        <Button
          onClick={handlePreview}
          color="primary"
          variant="outlined"
          disabled={
            !selectedTemplate || resolving || sending || previewing || !allFilled
          }
        >
          {previewing ? (
            <CircularProgress size={20} />
          ) : (
            i18n.t("documentTemplates.sendModal.preview")
          )}
        </Button>
        <Button
          onClick={handleSend}
          color="primary"
          variant="contained"
          disabled={
            !selectedTemplate || resolving || sending || previewing || !allFilled
          }
        >
          {sending ? (
            <CircularProgress size={20} />
          ) : (
            i18n.t("documentTemplates.sendModal.send")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentSendModal;
